import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, DataTable, Input } from '../../../../components/ui';
import { getRoles, roleKeys } from '../../../../api/role.api';
import { useInfiniteScroll } from '../../../../hooks/useInfiniteScroll';
import { useDebounce } from '../../../../hooks/useDebounce';
import type { Role } from '../../../../types/role.types';

const columns: ColumnsType<Role> = [
	{
		title: 'Title',
		dataIndex: 'title',
		key: 'title',
		width: 120,
		sorter: (a, b) => a.title.localeCompare(b.title),
	},
	{
		title: 'Permissions',
		key: 'permissions',
		width: 160,
		align: 'center',
		render: (_: unknown, record: Role) => record.permissions?.length ?? 0,
	},
	{
		title: 'Status',
		dataIndex: 'status',
		key: 'status',
		width: 120,
		align: 'center',
		render: (_: unknown, record: Role) => (
			<Tag color={record?.isActive ? 'green' : 'default'}>
				{record?.isActive ? 'Active' : 'Inactive'}
			</Tag>
		),
	},
	{
		title: 'Created At',
		dataIndex: 'createdAt',
		key: 'createdAt',
		width: 180,
		render: (date: string) => new Date(date).toLocaleDateString(),
	},
];

interface RoleListViewProps {
	createPermission?: string;
}

export default function RoleListView({ createPermission = 'iam:role:create' }: RoleListViewProps) {
	const navigate = useNavigate();
	const [search, setSearch] = useState('');
	const debouncedSearch = useDebounce(search, 400);

	const { items, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
		useInfiniteScroll<Role>({
			queryKey: roleKeys.lists(),
			queryFn: getRoles,
		});

	const filteredItems = useMemo(() => {
		if (!debouncedSearch) return items;
		const q = debouncedSearch.toLowerCase();
		return items.filter((r) => r.title.toLowerCase().includes(q));
	}, [items, debouncedSearch]);

	return (
		<div>
			<div className="flex items-center justify-between mb-6">
				<p className="text-sm text-gray-500 m-0">
					Define roles and their associated permissions.
				</p>
				<Button
					variant="primary"
					icon={<PlusOutlined />}
					permission={createPermission}
					onClick={() => navigate('/iam/roles/create')}
				>
					Create Role
				</Button>
			</div>

			<div className="mb-4 max-w-sm">
				<Input
					placeholder="Search roles..."
					prefix={<SearchOutlined />}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					allowClear
				/>
			</div>

			<DataTable<Role>
				columns={columns}
				dataSource={filteredItems}
				rowKey="id"
				loading={isLoading}
				onRowClick={(record) => navigate(`/iam/roles/${record.id}`)}
				infiniteScroll={{ isFetchingNextPage, hasNextPage, fetchNextPage }}
			/>
		</div>
	);
}
